/**
 * IconButton — el botón que es solo un ícono.
 *
 * ⚠️ Convive con `components/IconButton.tsx` (4 usos). La diferencia que
 * justifica el port: **`label` es obligatorio** y sale como `aria-label` y como
 * `title`. Un botón que es solo un ícono no dice nada a un lector de pantalla,
 * y el del repo deja la etiqueta opcional.
 *
 * `badge` es el punto rojo de «hay algo sin leer». Late con la misma animación
 * que el punto de `StatusBadge`, y las dos se apagan con
 * `prefers-reduced-motion`.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react'

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'aria-label'> {
  /** Obligatorio: es lo único que el lector de pantalla va a anunciar. */
  label: string
  icon: ReactNode
  /** Sin recuadro ni fondo, para las acciones dentro de una fila. */
  bare?: boolean
  /** El punto de «sin leer». */
  badge?: boolean
  className?: string
}

export function IconButton({
  label,
  icon,
  bare = false,
  badge = false,
  disabled = false,
  className = '',
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      className={`cl-iconbtn ${bare ? 'cl-iconbtn--bare ' : ''}${className}`.trim()}
      aria-label={label}
      title={label}
      disabled={disabled}
      {...rest}
    >
      <span aria-hidden="true" style={{ display: 'flex' }}>
        {icon}
      </span>
      {badge && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 6,
            right: 7,
            width: 7,
            height: 7,
            borderRadius: '50%',
            // ⚠️ `--color-danger` son canales en este repo: sin el `rgb()` el
            // navegador descarta la propiedad y el punto sale invisible.
            background: 'rgb(var(--color-danger))',
            animation: 'cl-pulse-dot 2s ease-in-out infinite',
          }}
        />
      )}
    </button>
  )
}
